/**
 * @fileoverview Native WebSocket Adapter with SignalR Event Emitter interface for ADSL.
 * Provides full drop-in compatibility with SignalRService API (`on`, `off`, `emit`, `connect`, `disconnect`, `getConnectionId`, `invoke`)
 * while connecting to ADSL's native WebSocket server (`REACT_APP_API_URL_WSS`).
 * 
 * @module utilities/WS/SignalRService
 */

class SignalRService {
    constructor() {
        this.connection = null;
        this.listeners = {};
        this.isConnected = false;
        this.connectionId = null;
        this.reconnectTimer = null;
        this.reconnectAttempts = 0;
        this.isExplicitDisconnect = false;
        this.lastConnectParams = {};
    }

    // Suscribirse a un evento
    on(event, callback) {
        if (!event || typeof callback !== 'function') return;
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
    }

    // Desuscribirse de un evento
    off(event, callback) {
        if (!event || !this.listeners[event]) return;
        if (!callback) {
            delete this.listeners[event];
            return;
        }
        this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }

    // Emitir un evento localmente a todos los escuchas registrados
    emit(event, data) {
        if (!event || !this.listeners[event]) return;
        const listenersCopy = [...this.listeners[event]];
        listenersCopy.forEach(callback => {
            try {
                callback(data);
            } catch (err) {
                // Ignore listener exceptions
            }
        });
    }

    // Iniciar la conexión persistente con ADSL WebSockets
    async connect(params = {}) {
        this.lastConnectParams = { ...params };
        this.isExplicitDisconnect = false;

        if (this.connection) {
            await this.disconnect();
        }

        // Generar ConnectionId único para la sesión
        this.connectionId = 'conn_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);

        // Generar URL del WebSocket a partir de REACT_APP_API_URL_WSS o fallback
        let wsUrl = process.env.REACT_APP_API_URL_WSS || '';
        if (!wsUrl) {
            if (process.env.REACT_APP_API_URL) {
                const httpUrl = process.env.REACT_APP_API_URL;
                wsUrl = httpUrl.replace(/^http(s)?:\/\//, 'ws$1://');
                if (wsUrl.endsWith('/api/')) {
                    wsUrl = wsUrl.replace(/\/api\/$/, '/ws');
                } else if (wsUrl.endsWith('/api')) {
                    wsUrl = wsUrl.replace(/\/api$/, '/ws');
                } else if (!wsUrl.endsWith('/ws')) {
                    wsUrl = wsUrl.endsWith('/') ? `${wsUrl}ws` : `${wsUrl}/ws`;
                }
            } else if (typeof window !== 'undefined' && window.location) {
                const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                wsUrl = `${protocol}//${window.location.host}/ws`;
            } else {
                wsUrl = 'ws://localhost:5000/ws';
            }
        }

        // Mapear tipos de SignalR a tipos de ADSL WebSockets
        let tipo = params.tipo || '';
        if (tipo === 'quickreaction:admin') {
            tipo = 'superadmin';
        } else if (tipo.startsWith('quickreaction:player') || tipo === 'quickreaction:fan') {
            tipo = 'overlay:quick-reaction:fan';
        }

        const queryParts = [];
        if (tipo) queryParts.push(`tipo=${encodeURIComponent(tipo)}`);
        if (params.uid) queryParts.push(`uid=${encodeURIComponent(params.uid)}`);
        const zonaParam = params.zona || params.userZone;
        if (zonaParam) queryParts.push(`zona=${encodeURIComponent(zonaParam)}`);
        if (params.roomId) queryParts.push(`roomId=${encodeURIComponent(params.roomId)}`);
        if (params.playerSide) queryParts.push(`side=${encodeURIComponent(params.playerSide)}`);

        const cleanBaseUrl = wsUrl.split('?')[0];
        const finalUrl = queryParts.length > 0 ? `${cleanBaseUrl}?${queryParts.join('&')}` : cleanBaseUrl;

        return new Promise((resolve, reject) => {
            try {
                const socket = new WebSocket(finalUrl);
                this.connection = socket;

                let hasOpened = false;

                socket.onopen = () => {
                    hasOpened = true;
                    this.isConnected = true;
                    this.reconnectAttempts = 0;
                    this.emit('connected', { connectionId: this.connectionId });
                    this.emit('reconnected', { connectionId: this.connectionId });
                    resolve();
                };

                socket.onmessage = (messageEvent) => {
                    try {
                        const rawData = messageEvent.data;
                        let envelope = null;
                        if (typeof rawData === 'string') {
                            envelope = JSON.parse(rawData);
                        } else {
                            envelope = rawData;
                        }

                        if (envelope) {
                            const eventType = envelope.type || envelope.Type;
                            const dataPayload = envelope.payload !== undefined
                                ? envelope.payload
                                : (envelope.data !== undefined ? envelope.data : envelope);

                            if (eventType) {
                                this.emit(eventType, dataPayload);
                                if (eventType.includes('.')) {
                                    this.emit(eventType.replace(/\./g, ':'), dataPayload);
                                } else if (eventType.includes(':')) {
                                    this.emit(eventType.replace(/:/g, '.'), dataPayload);
                                }
                            }
                            this.emit('message', envelope);
                            this.emit('ReceiveMessage', envelope);
                        }
                    } catch (err) {
                        // Ignore non-JSON packets
                    }
                };

                socket.onerror = (error) => {
                    this.emit('error', { error });
                    if (!hasOpened) {
                        reject(error);
                    }
                };

                socket.onclose = (closeEvent) => {
                    this.isConnected = false;
                    this.connection = null;
                    this.emit('close', { error: closeEvent });

                    if (!this.isExplicitDisconnect) {
                        this.emit('reconnecting', { attempt: this.reconnectAttempts + 1 });
                        this.scheduleReconnect();
                    }
                };
            } catch (err) {
                this.isConnected = false;
                this.connection = null;
                reject(err);
            }
        });
    }

    scheduleReconnect() {
        if (this.reconnectTimer || this.isExplicitDisconnect) return;

        this.reconnectAttempts += 1;
        const delay = Math.min(1000 * Math.pow(1.5, Math.min(this.reconnectAttempts, 6)), 10000);

        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            if (!this.isExplicitDisconnect) {
                try {
                    await this.connect(this.lastConnectParams);
                } catch (e) {
                    this.scheduleReconnect();
                }
            }
        }, delay);
    }

    // Detener la conexión limpiamente
    async disconnect() {
        this.isExplicitDisconnect = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.connection) {
            try {
                if (this.connection.readyState === WebSocket.OPEN || this.connection.readyState === WebSocket.CONNECTING) {
                    this.connection.close();
                }
            } catch (err) {
                // Ignore close error
            } finally {
                this.connection = null;
                this.isConnected = false;
            }
        }
    }

    // Obtener el identificador de conexión actual
    getConnectionId() {
        return this.connectionId || null;
    }

    // Invocar o enviar un comando/mensaje
    async invoke(methodName, ...args) {
        if (!this.connection || this.connection.readyState !== WebSocket.OPEN) {
            return;
        }

        try {
            const payload = {
                type: methodName,
                arguments: args,
                data: args.length === 1 ? args[0] : args
            };
            this.connection.send(JSON.stringify(payload));
        } catch (err) {
            // Ignore send error
        }
    }

    send(data) {
        if (this.connection && this.connection.readyState === WebSocket.OPEN) {
            try {
                this.connection.send(typeof data === 'string' ? data : JSON.stringify(data));
                return true;
            } catch (err) {
                return false;
            }
        }
        return false;
    }
}

const signalRServiceInstance = new SignalRService();
export default signalRServiceInstance;
